import React, { useLayoutEffect, useRef, useState } from "react";

import { gql, useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { makeStyles } from "tss-react/mui";

import client from "@/client";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import {
  AllergiesKind,
  CoachViewerIdDocument,
  CreateFoodsInput,
  FoodAddPortionDetailsFragment,
  FoodItemFragment,
  FoodSource,
} from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";
import { colors } from "@/theme/overrides/colors";

import { fetchEdamamFoods } from "./fetchEdamamFoods";
import FoodItem from "./FoodItem";
import SearchIcon from "../Icons/SearchIcon";

interface Props {
  className?: string;
  clientAllergies?: AllergiesKind[];
  onClose: () => void;
  onAddFood: (
    food: FoodItemFragment,
    foodPortion: FoodAddPortionDetailsFragment,
    amount: number
  ) => void;
}

type CreateFoodsMutationData = {
  createFoods: {
    foods: FoodItemFragment[];
  };
};

type CreateFoodsMutationVariables = {
  input: CreateFoodsInput;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    borderRadius: 0,
    padding: theme.spacing(4, 3),
  },
  title: {
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 700,
    color: colors.dark,
  },
  closeButton: {
    padding: theme.spacing(1),
    color: colors.gray,
    backgroundColor: "#E8EEF6",
  },
  checkboxLabel: {
    color: colors.gray,
  },
  addButton: {
    padding: theme.spacing(2),
    color: colors.gray,
    backgroundColor: "#E8EEF6",
  },
  helperText: {
    color: colors.gray,
    textAlign: "center",
  },
  list: {
    maxHeight: "calc(100vh - 290px)",
    overflow: "auto",
  },
}));

const LIMIT = 8;

const SOURCES_STORAGE_KEY = "foodDatabaseSelectedSources";

const DEFAULT_SOURCES = {
  FOOD_DATABASE: false,
  RECIPES: true,
  CNC_GC: false,
  MCCANCE_AND_WIDOWSONS: false,
  USDA: true,
  FNRI: true,
  FNRI_FCT: true,
  EDAMAM: false,
};

type SelectedSources = typeof DEFAULT_SOURCES;

// Coaches shouldn't have to re-tick their databases on every search — the
// panel remounts per cell, so the selection persists in localStorage. Stored
// values are merged over the defaults so sources added later keep theirs.
const loadSelectedSources = (): SelectedSources => {
  const merged = { ...DEFAULT_SOURCES };
  try {
    const stored = localStorage.getItem(SOURCES_STORAGE_KEY);
    if (!stored) return merged;
    const parsed: unknown = JSON.parse(stored);
    (Object.keys(merged) as (keyof SelectedSources)[]).forEach((key) => {
      const value = (parsed as Record<string, unknown>)?.[key];
      if (typeof value === "boolean") merged[key] = value;
    });
  } catch {
    // Corrupt or unavailable storage — fall back to the defaults.
  }
  return merged;
};

const saveSelectedSources = (sources: SelectedSources) => {
  try {
    localStorage.setItem(SOURCES_STORAGE_KEY, JSON.stringify(sources));
  } catch {
    // Storage unavailable (e.g. private mode); selection still works for the
    // current session.
  }
};

const foodDatabaseQuery = graphql(`
  query FoodDatabase(
    $sources: [FoodSource!]!
    $name: String!
    $first: Int
    $after: String
  ) {
    foods(name: $name, sources: $sources, first: $first, after: $after) {
      nodes {
        ...FoodItem
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const createFoodsMutation = gql`
  mutation CreateFoodsFromEdamam($input: CreateFoodsInput!) {
    createFoods(input: $input) {
      foods {
        id
        source
        sourceId
        category
        name
        epWeightGrams
        energyKcal
        carbohydrateGrams
        proteinGrams
        fatGrams
        allergies
        portions {
          id
          foodId
          amount
          unit
          unitWithModifier
          weightGrams
        }
      }
    }
  }
`;

const FoodDatabase: React.FC<Props> = ({
  className,
  onClose,
  onAddFood,
  clientAllergies = [],
}) => {
  const { classes, cx } = useStyles();
  const [selectedSources, setSelectedSources] =
    useState<SelectedSources>(loadSelectedSources);
  const [searchString, setSearchString] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<FoodItemFragment[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const requestIdRef = useRef(0);
  const setSearch = useDebounce(setSearchString, 500);

  const { data: coachViewerData } = useQuery(CoachViewerIdDocument);
  const [createFoods] = useMutation<
    CreateFoodsMutationData,
    CreateFoodsMutationVariables
  >(createFoodsMutation);

  const toggleSource = (source: keyof SelectedSources) => {
    const next = { ...selectedSources, [source]: !selectedSources[source] };
    setSelectedSources(next);
    saveSelectedSources(next);
  };

  const getGraphqlSources = (): FoodSource[] => {
    return Object.entries(selectedSources)
      .filter(([key, checked]) => checked && key !== "EDAMAM")
      .map(([key]) => key) as FoodSource[];
  };

  const persistEdamamFood = async (
    food: FoodItemFragment,
    foodPortion: FoodAddPortionDetailsFragment
  ): Promise<FoodItemFragment | null> => {
    const coachId = coachViewerData?.coachViewer.id;

    if (!coachId) return null;

    const result = await createFoods({
      variables: {
        input: {
          foodAttributes: [
            {
              coachId,
              sourceId: food.sourceId,
              source: food.source,
              energyKcal: food.energyKcal,
              epWeightGrams: food.epWeightGrams,
              fatGrams: food.fatGrams,
              name: food.name,
              proteinGrams: food.proteinGrams,
              carbohydrateGrams: food.carbohydrateGrams,
              category: food.category,
              portions: [
                {
                  amount: foodPortion.amount,
                  unit: foodPortion.unit,
                  weightGrams: foodPortion.weightGrams,
                },
              ],
            },
          ],
        },
      },
    });

    return result.data?.createFoods.foods[0] ?? null;
  };

  const searchFoods = async (cursor: string | null, loadMore: boolean) => {
    const requestId = ++requestIdRef.current;
    const graphqlSources = getGraphqlSources();
    const shouldSearchEdamam = selectedSources.EDAMAM && !loadMore;
    const shouldSearchGraphql = graphqlSources.length > 0;

    if (!shouldSearchEdamam && !shouldSearchGraphql) {
      setResults([]);
      setEndCursor(null);
      setHasNextPage(false);
      return;
    }

    setLoading(true);
    if (!loadMore) setHasNextPage(false);

    try {
      const [graphqlResult, edamamFoods] = await Promise.all([
        shouldSearchGraphql
          ? client.query({
              query: foodDatabaseQuery,
              variables: {
                sources: graphqlSources,
                name: searchString,
                first: LIMIT,
                after: cursor,
              },
            })
          : Promise.resolve(null),
        shouldSearchEdamam
          ? fetchEdamamFoods(searchString)
          : Promise.resolve([] as FoodItemFragment[]),
      ]);

      if (requestId !== requestIdRef.current) return;

      const graphqlFoods = graphqlResult?.data.foods.nodes ?? [];

      setResults((prev) =>
        loadMore ? prev.concat(graphqlFoods) : edamamFoods.concat(graphqlFoods)
      );
      setHasNextPage(graphqlResult?.data.foods.pageInfo.hasNextPage ?? false);
      setEndCursor(graphqlResult?.data.foods.pageInfo.endCursor ?? null);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;

      console.error("Unable to search foods", error);
      if (!loadMore) setResults([]);
      setHasNextPage(false);
      setEndCursor(null);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useLayoutEffect(() => {
    setEndCursor(null);
    if (searchString === "") {
      setResults([]);
      setHasNextPage(false);
      return;
    }

    searchFoods(null, false);
  }, [searchString, selectedSources]);

  const searchMore = () => {
    searchFoods(endCursor, true);
  };

  const handleAddFood = async (
    food: FoodItemFragment,
    foodPortion: FoodAddPortionDetailsFragment,
    amount: number
  ) => {
    if (food.source !== "edamam") {
      onAddFood(food, foodPortion, amount);
      return;
    }

    setLoading(true);
    try {
      const persistedFood = await persistEdamamFood(food, foodPortion);
      const persistedPortion = persistedFood?.portions[0];

      if (!persistedFood || !persistedPortion) return;

      onAddFood(persistedFood, persistedPortion, amount);
    } catch (error) {
      console.error("Unable to persist Edamam food", error);
    } finally {
      setLoading(false);
    }
  };

  const noResults = !loading && searchString !== "" && isEmpty(results);

  return (
    <Paper className={cx(classes.root, className)} elevation={0}>
      <Stack spacing={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography className={classes.title}>Select Items</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Grid container>
          {Features.foodDatabaseSourcesEnabled && (
            <>
              <Grid item xs={4}>
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedSources.FOOD_DATABASE}
                      onChange={() => toggleSource("FOOD_DATABASE")}
                    />
                  }
                  label="Food Database"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedSources.CNC_GC}
                      onChange={() => toggleSource("CNC_GC")}
                    />
                  }
                  label="CNC,GC"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedSources.MCCANCE_AND_WIDOWSONS}
                      onChange={() => toggleSource("MCCANCE_AND_WIDOWSONS")}
                    />
                  }
                  label="McCance & Widowsons"
                />
              </Grid>
            </>
          )}
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.RECIPES}
                  onChange={() => toggleSource("RECIPES")}
                />
              }
              label="My Recipes"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.USDA}
                  onChange={() => toggleSource("USDA")}
                />
              }
              label="USDA"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.FNRI}
                  onChange={() => toggleSource("FNRI")}
                />
              }
              label="FNRI FEL"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.FNRI_FCT}
                  onChange={() => toggleSource("FNRI_FCT")}
                />
              }
              label="FNRI FCT"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.EDAMAM}
                  onChange={() => toggleSource("EDAMAM")}
                />
              }
              label="Edamam"
            />
          </Grid>
        </Grid>
        <Stack>
          <TextField
            placeholder="Search..."
            size="small"
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="inherit" />
                </InputAdornment>
              ),
            }}
          />
          <List className={classes.list}>
            {noResults && (
              <Stack direction="row" justifyContent="center">
                <Typography className={classes.helperText}>
                  No results were found for &quot;{searchString}&quot;
                </Typography>
              </Stack>
            )}
            {results.map((food) => (
              <FoodItem
                key={`${food.source}-${food.id}-${food.sourceId}`}
                food={food}
                onAddFood={handleAddFood}
                clientAllergies={clientAllergies}
              />
            ))}
            {loading && (
              <Stack direction="row" justifyContent="center">
                <CircularProgress variant="indeterminate" size={24} />
              </Stack>
            )}
            {hasNextPage && (
              <Stack direction="row" justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={searchMore}
                  disabled={!hasNextPage}
                >
                  Load more
                </Button>
              </Stack>
            )}
          </List>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default FoodDatabase;
